import { useState } from "react";
import useSWR from "swr";

import { api, PUBLIC_API_URL, TokenInfo, UserMe } from "../api";
import { AgentCallout, Icon, IconName, PageHeader } from "../ui";
import { formatDate, TokenStatus } from "./Tokens";

const fetcher = (path: string) => api.get<TokenInfo[]>(path);

export const CURL = `curl -H "Authorization: Bearer $NH_API_TOKEN" \\
  ${PUBLIC_API_URL}/api/v1/metadata/indices`;

const AGENT_PROMPT = `Use the NilssonHedge API (NH API) for hedge fund and strategy index data.

- Base URL: ${PUBLIC_API_URL}/api/v1
- Auth: send "Authorization: Bearer $NH_API_TOKEN" on every request. Read the token from the NH_API_TOKEN environment variable; never print it or ask me to paste it.
- OpenAPI spec: ${PUBLIC_API_URL}/api/v1/openapi.json (human docs: ${PUBLIC_API_URL}/docs)
- Rate limits depend on my plan; on HTTP 429, wait and retry.

Start with GET /users/me to confirm access and see my plan limits.`;

const RESOURCES: { href: string; icon: IconName; title: string; body: string; external?: boolean }[] = [
  { href: `${PUBLIC_API_URL}/docs`, icon: "book", title: "API reference", body: "Every endpoint, parameter and response schema.", external: true },
  { href: "/indices", icon: "chart", title: "Indices", body: "NilssonHedge strategy indices, available on every plan." },
  { href: "/watchlists", icon: "star", title: "Watchlists", body: "Group the funds you follow and export their data." },
  { href: "/export", icon: "download", title: "Data export", body: "Download fund metadata and return series as CSV." },
];

export function Dashboard({ user }: { user: UserMe }) {
  const { data: tokens } = useSWR<TokenInfo[]>("/tokens", fetcher);

  return (
    <section className="flex flex-col gap-10">
      <PageHeader
        title="Dashboard"
        description={`Welcome${user.name ? `, ${user.name}` : ""}. Programmatic access to the NilssonHedge fund database, on your ${user.tier.display_name} plan.`}
      />

      <div>
        <h2 className="text-lg font-semibold text-main mb-5">Get started</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <Card icon="code" title="Make your first request">
            <p>Create a token, export it as NH_API_TOKEN, then run the request below.</p>
          </Card>
          <Card icon="sparkles" title="Set up with your AI agent">
            <p>Works with Claude Code, Codex, or any agent that can make HTTP requests.</p>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 font-medium">
              <CopyButton text={AGENT_PROMPT} label="Copy prompt" plain />
              <a href={`${PUBLIC_API_URL}/api/v1/openapi.json`} target="_blank" rel="noopener noreferrer">OpenAPI spec ↗</a>
            </div>
          </Card>
        </div>
        <div className="relative mt-6">
          <pre className="p-4 pr-20 rounded-lg bg-stat border border-border text-[13px] leading-relaxed font-mono text-main whitespace-pre-wrap break-all">{CURL}</pre>
          <CopyButton text={CURL} className="absolute top-3 right-3" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-x-8 gap-y-6 pt-10 border-t border-border">
        {RESOURCES.map((r) => (
          <Card key={r.href} icon={r.icon} title={`${r.title} ${r.external ? "↗" : "→"}`} href={r.href} external={r.external}>
            <p>{r.body}</p>
          </Card>
        ))}
      </div>

      <div className="flex flex-col gap-5 pt-10 border-t border-border">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-main">API tokens</h2>
          <a href="/tokens" className="btn-default text-main">Manage tokens</a>
        </div>
        {!tokens && <p className="text-sm text-muted">Loading…</p>}
        {tokens && tokens.length === 0 && (
          <div className="flex flex-col items-center gap-3 p-10 rounded-lg border border-border text-center">
            <Icon name="key" className="w-8 h-8 text-muted" />
            <p className="text-[15px] font-semibold text-main">No tokens yet</p>
            <p className="text-sm text-muted">Create one to start calling the API.</p>
            <a href="/tokens" className="btn-primary text-white mt-1">
              <Icon name="plus" className="w-4 h-4" />
              New token
            </a>
          </div>
        )}
        {tokens && tokens.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Expires</th>
                  <th className="max-sm:hidden">Last used</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.id}>
                    <td className="text-main font-medium whitespace-nowrap">{t.name}</td>
                    <td className="whitespace-nowrap">{formatDate(t.expires_at)}</td>
                    <td className="max-sm:hidden whitespace-nowrap">{t.last_used_at ? formatDate(t.last_used_at) : "Never"}</td>
                    <td><TokenStatus token={t} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <AgentCallout />
      </div>
    </section>
  );
}

function Card({
  icon,
  title,
  href,
  external,
  children,
}: {
  icon: IconName;
  title: string;
  href?: string;
  external?: boolean;
  children: React.ReactNode;
}) {
  const heading = <h3 className="text-[15px] font-semibold text-main">{title}</h3>;
  return (
    <div className="flex gap-4 min-w-0">
      <div className="flex items-center justify-center w-12 h-12 shrink-0 rounded-lg border border-border bg-card text-accent">
        <Icon name={icon} className="w-6 h-6" />
      </div>
      <div className="min-w-0 flex-1 text-sm text-muted leading-relaxed">
        {href ? (
          <a href={href} className="hover:underline text-main" {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
            {heading}
          </a>
        ) : (
          heading
        )}
        <div className="mt-1">{children}</div>
      </div>
    </div>
  );
}

function CopyButton({ text, label = "Copy", plain = false, className = "" }: { text: string; label?: string; plain?: boolean; className?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={copy}
      className={`${plain ? "text-accent hover:text-accent-hover font-medium cursor-pointer" : "btn-sm btn-default"} ${className}`}
    >
      {copied ? "Copied" : label}
    </button>
  );
}
